import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Web3 from 'web3';
import GridLayout from 'react-grid-layout';
import './Auction.css';

const Auction = ({wpApiUrl, auctionContract}) => {
  const defaultBid = 0.001
  const artStateMap = new Map([
    [0, 'New'],
    [1, 'Opened'],
    [2, 'Minted'],
    [3, 'Closed'],
  ]);

  const [art, setArt] = React.useState([]);
  const [filterBy, setFilterBy] = React.useState(0);
  const [sortBy, setSortBy] = React.useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [bid, setBid] = useState(defaultBid);
  const [status, setStatus] = useState('');
  const [numCols, setNumCols] = useState(calculateCols());
  const [fullScreenImage, setFullScreenImage] = useState(null);

  useEffect(() => {
    const fetchSubmissions = async () => {
      //console.log("filter: " + filterBy + " sort: " + sortBy);
      const [submissionHashes, highestBid] = await Promise.all([
        auctionContract.methods.listSubmissions(Number(filterBy), Number(sortBy)).call(),
        auctionContract.methods.getHighestBid().call(),
      ]);

      const total = Math.ceil(submissionHashes.length / pageSize);
      setTotalPages(total);
      const currentPageHashes = submissionHashes.slice((page - 1) * pageSize, page * pageSize);

      const highestEthBid = Number(Web3.utils.fromWei(highestBid[1].toString(), 'ether'));
      setBid(highestEthBid+defaultBid);

      if (currentPageHashes.length === 0) {
        setArt([]);
        return;
      }

      const [submissions, fileResponses] = await Promise.all([
        auctionContract.methods.getSubmissions(currentPageHashes).call(),
        axios.post(wpApiUrl + "/artifex/v1/art-list", { hashes: currentPageHashes }),
      ]).catch(error => {
        setStatus(String(error));
        return [false, false];
      });

      if (submissions === false) {
        return {};
      }

      const currentPageArt = currentPageHashes.map((hash, index) => {
        return { ...submissions[index],
                 hash,
                 fileUrl: fileResponses.data[hash].guid,
                 author: fileResponses.data[hash].author,
                 ethBid: Web3.utils.fromWei(submissions[index].bidAmount.toString(), 'ether') };
      });

      //console.log(currentPageArt);
      setArt(currentPageArt);
    };

    fetchSubmissions();
  }, [wpApiUrl, auctionContract, page, pageSize, filterBy, sortBy]);

  useEffect(() => {
    const handleResize = () => {
      setNumCols(calculateCols());
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  function handlePageChange(newPage) {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  }

  async function handleBid(submissionHash) {
    try {
      if (typeof window.ethereum === 'undefined') {
        throw new Error("MetaMask is not installed");
      }

      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      if (accounts.length === 0) {
        throw new Error("No accounts found");
      }
      const account = accounts[0];
      const wei = Web3.utils.toWei(bid.toString(), 'ether');

      auctionContract.methods.placeBid(submissionHash)
                     .send({ from: account, value: wei })
                     .on('transactionHash', hash => {
                       console.log("Submit bid transaction hash: " + hash);
                       setStatus("Transaction hash: " + hash);
                     })
                     .on('confirmation', (confirmationNumber, receipt) => {
                       setStatus("Transaction hash: " + String(receipt.transactionHash) + " confirmed!");
                     })
                     .on('error', error => {
                       throw error;
                     });
    } catch (error) {
      console.log(String(error));
      setStatus(String(error));
    }
  }

  function handleImageClick(image) {
    setFullScreenImage(image);
  };

  function handleCloseFullScreen() {
    setFullScreenImage(null);
  };

  function calculateCols() {
    const width = window.innerWidth;
    if (width < 600) {
      return 4; // 2 columns for small screens
    } else if (width < 900) {
      return 6; // 3 columns for medium screens
    } else {
      return 8; // 4 columns for large screens
    }
  };
  //const numCols = calculateCols();

  function shortenAddress(address) {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  return (
    <div className="auction-container">
      {fullScreenImage && (
        <div className="full-screen-image-container">
          <img src={fullScreenImage} className="full-screen-image" />
          <button className="close-button" onClick={handleCloseFullScreen}>X</button>
        </div>
      )}
      <div className="bid-filter-container">
        <div className="bid-container">
          <p className="place-bid"><span>Place Bid: </span>
            <input
              type="text"
              value={bid}
              onChange={(e) => setBid(e.target.value)}
              placeholder="Bid amount"
              className="bid-input"
            />
          </p>
        </div>
        <div className="filter-sort-container">
          <label className="filter-label">Filter by:
            <select className="filter-select" onChange={(e) => setFilterBy(e.target.value)}>
              <option value="0">None</option>
              <option value="1">Opened</option>
              <option value="2">Minted</option>
            </select>
          </label>
          <label className="sort-label">Sort by:
            <select className="sort-select" onChange={(e) => setSortBy(e.target.value)}>
              <option value="0">None</option>
              <option value="1">State</option>
              <option value="2">Bid</option>
            </select>
          </label>
        </div>
      </div>
      <GridLayout
        className="art-grid"
        cols={numCols}
        rowHeight={250}
        width={window.innerWidth}
        margin={[0, 0]}
        padding={[20, 20]}
        isResizable={false}
        isDraggable={false}
        autoSize={true}
      >
        {art.map((art, index) => (
          <div key={index} data-grid={{ x: (index % numCols), y: Math.floor(index / numCols), w: 1, h: 1,}} className="art-item">
            <img src={art.fileUrl}
                 width={128}
                 height={128}
                 alt={"Submission hash: " + art.hash}
                 onClick={() => handleImageClick(art.fileUrl)}
            />
            <p className="art-item-artist-name">
              <span>Artist: </span>{art.author ? art.author : shortenAddress(art.submitter)}
            </p>
            {Number(art.state) === 1 ? (
              <>
                <p className="art-item-bid"><span>Current Bid: </span>{art.ethBid} ETH</p>
                <button onClick={() => handleBid(art.hash)} className="bid-button">Bid</button>
              </>
            ) : (
              <>
                <p className="art-item-bid"><span>{Number(art.state) === 2 ? ("Winning"):("Last")} Bid: </span>{art.ethBid} ETH</p>
                <p className="art-item-state">{artStateMap.get(Number(art.state))}</p>
              </>
            )}
          </div>
        ))}
      </GridLayout>
      <div className="pagination-container">
        <button onClick={() => handlePageChange(page - 1)} className="pagination-button">Previous</button>
        Page {page} of {totalPages}
        <button onClick={() => handlePageChange(page + 1)} className="pagination-button">Next</button>
        <input
          type="number"
          value={page}
          onChange={(e) => handlePageChange(e.target.valueAsNumber)}
          min="1"
          max={totalPages}
          className="pagination-input"
        />
      </div>
      <p className="status-text">{status}</p>
    </div>
  );
};

export default Auction
